import { Button, Grid, Group, Paper, Stack, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { showNotification } from "@mantine/notifications";
import dayjs from "dayjs";
import { Bar, Line } from "react-chartjs-2";
import React from "react";
import { useParams } from "react-router-dom";
import { GET_INSTAGRAM_GROWTH } from "../../services/instagram_analytics";
import {
    AnalyticsEmptyState,
    extractGrowthRows,
    InstagramAnalyticsHeader,
    StatCard,
    formatDate,
    formatNumber,
    getDefaultDateRange,
    getSyncResultMessage,
    useInstagramAccounts,
    useInstagramSync,
} from "./shared";

export default function InstagramAnalyticsGrowthPage() {
    const { oauthId } = useParams();
    const { accounts, loadingAccounts } = useInstagramAccounts();
    const [loading, setLoading] = React.useState<boolean>(true);
    const [growth, setGrowth] = React.useState<any[]>([]);

    const [dateRange, setDateRange] = React.useState<[Date | null, Date | null]>(() => {
        const [start, end] = getDefaultDateRange(30);
        return [dayjs(start).toDate(), dayjs(end).toDate()];
    });

    const loadData = React.useCallback(async () => {
        if (!oauthId) return;

        try {
            setLoading(true);
            const dateStart = dateRange[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : undefined;
            const dateEnd = dateRange[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : undefined;

            const response = await GET_INSTAGRAM_GROWTH(oauthId, { dateStart, dateEnd });
            const rows = extractGrowthRows(response);
            setGrowth(rows);
            getSyncResultMessage(rows.length === 0);
        } catch (err: any) {
            showNotification({ message: err.message, color: "red" });
        } finally {
            setLoading(false);
        }
    }, [oauthId, dateRange]);

    React.useEffect(() => {
        loadData();
    }, [loadData]);

    const { syncing, triggerSync } = useInstagramSync(oauthId, loadData);

    const orderedGrowth = React.useMemo(
        () => [...growth].sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf()),
        [growth]
    );

    const growthVariation = React.useMemo(() => {
        return orderedGrowth.map((item, index) => {
            if (index === 0) {
                return {
                    date: item.date,
                    delta: 0,
                };
            }

            const previous = Number(orderedGrowth[index - 1]?.followers_count || 0);
            const current = Number(item?.followers_count || 0);

            return {
                date: item.date,
                delta: current - previous,
            };
        });
    }, [orderedGrowth]);

    const first = orderedGrowth[0]?.followers_count || 0;
    const last = orderedGrowth[orderedGrowth.length - 1]?.followers_count || 0;
    const netGrowth = last - first;
    const growthPercent = first > 0 ? (netGrowth / first) * 100 : 0;

    const peakDay = [...orderedGrowth].sort((a, b) => (b.followers_count || 0) - (a.followers_count || 0))[0];
    const isEmpty = !loading && orderedGrowth.length === 0;

    return (
        <Stack>
            <InstagramAnalyticsHeader
                oauthId={oauthId}
                title="Relatório de crescimento"
                subtitle="Tendência de seguidores com filtros de data"
                active="growth"
                accounts={accounts}
                loadingAccounts={loadingAccounts}
                syncing={syncing}
                onSync={triggerSync}
            />

            <Paper withBorder p="md">
                <Group>
                    <DatePickerInput
                        type="range"
                        value={dateRange}
                        onChange={(value) => setDateRange(value as [Date | null, Date | null])}
                        valueFormat="DD/MM/YYYY"
                        allowSingleDateInRange
                        label="Período"
                    />
                    <Button mt={24} onClick={loadData}>Aplicar</Button>
                </Group>
            </Paper>

            {isEmpty ? (
                <AnalyticsEmptyState oauthId={oauthId} syncing={syncing} onSync={triggerSync} />
            ) : (
                <>
                    <Grid>
                        <Grid.Col md={4}>
                            <StatCard label="Crescimento líquido" value={formatNumber(netGrowth)} color={netGrowth >= 0 ? "green" : "red"} />
                        </Grid.Col>
                        <Grid.Col md={4}>
                            <StatCard label="Crescimento %" value={`${growthPercent.toFixed(2)}%`} />
                        </Grid.Col>
                        <Grid.Col md={4}>
                            <StatCard
                                label="Pico"
                                value={peakDay ? `${formatDate(peakDay.date)} (${formatNumber(peakDay.followers_count)})` : "-"}
                            />
                        </Grid.Col>
                    </Grid>

                    <Paper withBorder p="md">
                        <Text fw={600} mb="sm">Variação diária de seguidores</Text>
                        <Bar
                            options={{
                                responsive: true,
                                plugins: {
                                    legend: { position: "top" as const },
                                },
                                scales: {
                                    y: {
                                        ticks: {
                                            precision: 0,
                                        },
                                    },
                                },
                            }}
                            data={{
                                labels: growthVariation.map((item) => item.date),
                                datasets: [
                                    {
                                        label: "Crescimento diário",
                                        data: growthVariation.map((item) => item.delta),
                                        backgroundColor: growthVariation.map((item) =>
                                            item.delta >= 0 ? "#16a34a99" : "#dc262699"
                                        ),
                                        borderColor: growthVariation.map((item) =>
                                            item.delta >= 0 ? "#16a34a" : "#dc2626"
                                        ),
                                        borderWidth: 1,
                                    },
                                ],
                            }}
                        />
                        <Text size="xs" mt="xs" c="dimmed">
                            Valores negativos indicam perda de seguidores no dia.
                        </Text>
                    </Paper>

                    <Paper withBorder p="md">
                        <Text fw={600} mb="sm">Total de seguidores ao longo do tempo</Text>
                        <Line
                            options={{
                                responsive: true,
                                plugins: {
                                    legend: { position: "top" as const },
                                },
                                scales: {
                                    yAxes: {
                                        min: 0,
                                    }
                                }
                            }}
                            data={{
                                labels: orderedGrowth.map((item) => item.date),
                                datasets: [
                                    {
                                        label: "Seguidores",
                                        data: orderedGrowth.map((item) => item.followers_count || 0),
                                        borderColor: "#2563eb",
                                        backgroundColor: "#2563eb22",
                                        fill: true,
                                        tension: 0.3,
                                    },
                                ],
                            }}
                        />
                        <Text size="xs" mt="xs" c="dimmed">
                            Use o filtro de período para ampliar e filtrar os dados históricos.
                        </Text>
                    </Paper>
                </>
            )}
        </Stack>
    );
}
